import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ChartBar, Users, Ticket, Gear, SignOut, Eye,
  Cards, Star, Hash, SpinnerGap, Plus, Trash,
  ToggleLeft, ToggleRight, MagnifyingGlass,
  ArrowLeft, TrendUp, Clock, CurrencyCircleDollar,
  TelegramLogo, PaperPlaneTilt, Play, Pause, FilmSlate,
  ShieldWarning, ShieldCheck, ShieldSlash, Lock, Warning,
  Article, PencilSimple, Check, X, Globe, Tag
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin Login
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.success) {
        localStorage.setItem('admin_token', res.data.token);
        onLogin(res.data.token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white tracking-wide">ONIMA</h1>
          <p className="text-gray-500 text-sm mt-1">Admin Panel</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-3 py-2 rounded-lg">{error}</div>}
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:border-amber-500 focus:outline-none"
            data-testid="admin-username-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:border-amber-500 focus:outline-none"
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? <SpinnerGap size={20} className="animate-spin mx-auto" /> : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Stat Card
const StatCard = ({ icon: Icon, label, value, sub, color = "text-amber-400" }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <Icon size={20} className={color} />
      <span className="text-gray-400 text-sm">{label}</span>
    </div>
    <p className="text-3xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

// Dashboard Tab
const DashboardTab = ({ analytics, period, setPeriod, onResetStats }) => {
  if (!analytics) return <div className="text-gray-500 text-center py-20">Loading...</div>;

  const o = analytics.overview;
  const d = analytics.divinations;

  const periods = [
    { id: 'all', label: 'All Time' },
    { id: 'month', label: 'Month' },
    { id: 'week', label: 'Week' },
    { id: 'today', label: 'Today' },
  ];

  return (
    <div className="space-y-6">
      {/* Period Filter */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {periods.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${period === p.id ? 'bg-amber-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
              data-testid={`period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={onResetStats}
          className="text-xs text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 px-3 py-1.5 rounded-lg transition-colors"
          data-testid="reset-stats-btn"
        >
          Reset Test Data
        </button>
      </div>

      {/* Overview */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Overview {period !== 'all' && <span className="text-amber-400 text-sm font-normal">({periods.find(p => p.id === period)?.label})</span>}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Users" value={o.total_users} sub={`+${o.today_users} today, +${o.week_users} this week`} />
          <StatCard icon={Eye} label="Total Sessions" value={o.total_sessions} sub={`+${o.today_sessions} today`} color="text-purple-400" />
          <StatCard icon={Cards} label="Divinations" value={o.total_divinations} color="text-cyan-400" />
          <StatCard icon={Clock} label="Avg Session" value={`${o.avg_session_duration}s`} sub="avg duration" color="text-green-400" />
        </div>
      </div>

      {/* Divination Breakdown */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Divination Methods</h2>
        <div className="grid grid-cols-3 gap-4">
          <StatCard icon={Cards} label="Tarot" value={d.tarot} color="text-amber-400" />
          <StatCard icon={Star} label="Horoscope" value={d.horoscope} color="text-purple-400" />
          <StatCard icon={Hash} label="Numerology" value={d.numerology} color="text-cyan-400" />
        </div>
      </div>

      {/* Topic Popularity */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Topic Popularity</h2>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          {analytics.topics.length > 0 ? (
            <div className="space-y-3">
              {analytics.topics.map((topic) => {
                const maxCount = analytics.topics[0]?.count || 1;
                return (
                  <div key={topic.id} className="flex items-center gap-3">
                    <span className="text-sm text-gray-300 w-24 capitalize">{topic.id}</span>
                    <div className="flex-1 h-6 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-600 to-amber-400 rounded-full transition-all"
                        style={{ width: `${(topic.count / maxCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-400 w-10 text-right">{topic.count}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No session data yet</p>
          )}
        </div>
      </div>

      {/* User Events */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">User Behavior (Last 7 days)</h2>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          {analytics.events.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {analytics.events.map((ev) => (
                <div key={ev.event} className="bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-white">{ev.count}</p>
                  <p className="text-xs text-gray-400 mt-1">{ev.event.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No event data yet</p>
          )}
        </div>
      </div>

      {/* Daily Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Daily Registrations</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            {analytics.daily_registrations.length > 0 ? (
              <div className="space-y-2">
                {analytics.daily_registrations.map((d) => (
                  <div key={d.date} className="flex justify-between text-sm">
                    <span className="text-gray-400">{d.date}</span>
                    <span className="text-white font-medium">{d.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No data</p>
            )}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Credits Distribution</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Free Credits (active)</span>
              <span className="text-amber-400 font-medium">{analytics.credits.total_free}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Referral Credits</span>
              <span className="text-purple-400 font-medium">{analytics.credits.total_referral}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Purchased Credits</span>
              <span className="text-green-400 font-medium">{analytics.credits.total_purchased}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Promo Codes Tab
const PromoCodesTab = ({ token }) => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [newCredits, setNewCredits] = useState(50);
  const [newUses, setNewUses] = useState(100);
  const [newExpires, setNewExpires] = useState('');
  const [newNote, setNewNote] = useState('');
  const [editData, setEditData] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const loadPromos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, { headers });
      setPromos(res.data.promo_codes);
    } catch (err) {
      void 0;
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPromos(); }, [loadPromos]);

  const createPromo = async () => {
    if (!newCode.trim()) return;
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: newCode,
        credits_amount: newCredits,
        uses_left: newUses,
        expires_at: newExpires || null,
        note: newNote || null,
      }, { headers });
      setNewCode(''); setNewExpires(''); setNewNote('');
      setShowCreate(false);
      loadPromos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const startEdit = (promo) => {
    setEditId(promo.id);
    setEditData({
      credits_amount: promo.credits_amount,
      uses_left: promo.uses_left,
      expires_at: promo.expires_at ? promo.expires_at.substring(0, 10) : '',
      note: promo.note || '',
    });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${id}`, {
        credits_amount: editData.credits_amount,
        uses_left: editData.uses_left,
        expires_at: editData.expires_at || null,
        note: editData.note || '',
      }, { headers });
      setEditId(null);
      loadPromos();
    } catch (err) {
      alert('Update failed');
    }
  };

  const togglePromo = async (promo) => {
    await axios.put(`${API}/admin/promo-codes/${promo.id}`, { is_active: !promo.is_active }, { headers });
    loadPromos();
  };

  const deletePromo = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    await axios.delete(`${API}/admin/promo-codes/${id}`, { headers });
    loadPromos();
  };

  const isExpired = (p) => p.expires_at && new Date(p.expires_at) < new Date();

  if (loading) return <div className="text-gray-500 text-center py-20"><SpinnerGap size={32} className="animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Promo Codes</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
          data-testid="create-promo-btn"
        >
          <Plus size={16} /> New Code
        </button>
      </div>

      {showCreate && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-3" data-testid="create-promo-form">
          <input
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            placeholder="Code (e.g. WELCOME50)"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder:text-gray-500 focus:border-amber-500 focus:outline-none text-sm"
            data-testid="promo-code-input"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Credits</label>
              <input type="number" value={newCredits} onChange={(e) => setNewCredits(Number(e.target.value))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:outline-none text-sm" data-testid="promo-credits-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Max Uses</label>
              <input type="number" value={newUses} onChange={(e) => setNewUses(Number(e.target.value))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:outline-none text-sm" data-testid="promo-uses-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Expires (optional)</label>
              <input type="date" value={newExpires} onChange={(e) => setNewExpires(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:outline-none text-sm" data-testid="promo-expires-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Note (who/why)</label>
              <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="e.g. For blogger @name"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder:text-gray-500 focus:border-amber-500 focus:outline-none text-sm" data-testid="promo-note-input" />
            </div>
          </div>
          <button onClick={createPromo} className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm transition-colors" data-testid="promo-save-btn">
            Create
          </button>
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400">
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Credits</th>
              <th className="text-left p-3">Uses</th>
              <th className="text-left p-3">Expires</th>
              <th className="text-left p-3">Note</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                {editId === p.id ? (
                  <>
                    <td className="p-3 text-white font-mono">{p.code}</td>
                    <td className="p-3"><input type="number" value={editData.credits_amount} onChange={(e) => setEditData({...editData, credits_amount: Number(e.target.value)})}
                      className="w-16 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-amber-400 text-sm" /></td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 text-xs">{p.total_uses || 0}/</span>
                        <input type="number" value={editData.uses_left} onChange={(e) => setEditData({...editData, uses_left: Number(e.target.value)})}
                          className="w-14 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm" />
                      </div>
                    </td>
                    <td className="p-3"><input type="date" value={editData.expires_at} onChange={(e) => setEditData({...editData, expires_at: e.target.value})}
                      className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm" /></td>
                    <td className="p-3"><input value={editData.note} onChange={(e) => setEditData({...editData, note: e.target.value})} placeholder="Note..."
                      className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm" /></td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${p.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {p.is_active ? 'Active' : 'Off'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => saveEdit(p.id)} className="text-green-400 hover:text-green-300 text-xs bg-green-500/10 px-2 py-1 rounded">Save</button>
                        <button onClick={() => setEditId(null)} className="text-gray-400 hover:text-white text-xs">Cancel</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-3 text-white font-mono">{p.code}</td>
                    <td className="p-3 text-amber-400">{p.credits_amount}</td>
                    <td className="p-3 text-gray-300">{p.total_uses || 0} / {p.uses_left}</td>
                    <td className="p-3">
                      {p.expires_at ? (
                        <span className={`text-xs ${isExpired(p) ? 'text-red-400' : 'text-gray-400'}`}>
                          {p.expires_at.substring(0, 10)}{isExpired(p) && ' (expired)'}
                        </span>
                      ) : <span className="text-gray-600 text-xs">--</span>}
                    </td>
                    <td className="p-3 text-gray-400 text-xs max-w-[120px] truncate">{p.note || '--'}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${p.is_active && !isExpired(p) ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {isExpired(p) ? 'Expired' : p.is_active ? 'Active' : 'Off'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => startEdit(p)} className="text-gray-400 hover:text-amber-400 transition-colors" data-testid={`edit-promo-${p.code}`}>
                          <Gear size={16} />
                        </button>
                        <button onClick={() => togglePromo(p)} className="text-gray-400 hover:text-white transition-colors" data-testid={`toggle-promo-${p.code}`}>
                          {p.is_active ? <ToggleRight size={22} className="text-green-400" /> : <ToggleLeft size={22} />}
                        </button>
                        <button onClick={() => deletePromo(p.id)} className="text-gray-400 hover:text-red-400 transition-colors" data-testid={`delete-promo-${p.code}`}>
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {promos.length === 0 && (
              <tr><td colSpan={7} className="text-center text-gray-500 py-8">No promo codes yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Users Tab (stub with basic list)
const UsersTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [authFilter, setAuthFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [creditsFilter, setCreditsFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 50, sort_by: sortBy, sort_order: sortOrder });
      if (search) params.set('search', search);
      if (authFilter) params.set('auth_provider', authFilter);
      if (verifiedFilter) params.set('verified', verifiedFilter);
      if (creditsFilter) params.set('has_credits', creditsFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
    } catch (err) {
      void 0;
    } finally {
      setLoading(false);
    }
  }, [token, page, search, sortBy, sortOrder, authFilter, verifiedFilter, creditsFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications?unread_only=true`, { headers });
      setNotifications(res.data.notifications || []);
    } catch (err) {}
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const loadUserDetail = async (userId) => {
    setSelectedUser(userId);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers });
      setUserDetail(res.data);
    } catch (err) {
      void 0;
    } finally {
      setDetailLoading(false);
    }
  };

  const updateCredits = async (userId, field, value) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/credits`, { [field]: parseInt(value) }, { headers });
      loadUserDetail(userId);
      loadUsers();
    } catch (err) {
      alert('Failed to update credits');
    }
  };

  const toggleUserStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { is_active: !isActive }, { headers });
      loadUsers();
      if (userDetail) loadUserDetail(userId);
    } catch (err) {
      alert('Failed to toggle status');
    }
  };

  const markNotificationsRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/mark-read`, {}, { headers });
      setNotifications([]);
    } catch (err) {}
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return <span className="ml-1 text-amber-400">{sortOrder === 'desc' ? '↓' : '↑'}</span>;
  };

  // User detail panel
  if (selectedUser && userDetail) {
    const u = userDetail.user;
    return (
      <div className="space-y-4" data-testid="user-detail-panel">
        <button onClick={() => { setSelectedUser(null); setUserDetail(null); }} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm" data-testid="back-to-users-btn">
          <ArrowLeft size={16} /> Back to Users
        </button>

        {detailLoading ? <div className="text-center py-12"><SpinnerGap size={32} className="animate-spin text-gray-500" /></div> : (
          <>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white" data-testid="user-detail-name">{u.name || 'No name'}</h2>
                  <p className="text-gray-400 text-sm">{u.email}</p>
                  <div className="flex gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.auth_provider === 'google' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-400'}`}>
                      {u.auth_provider || 'email'}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.email_verified ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {u.email_verified ? 'Verified' : 'Unverified'}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {u.is_active !== false ? 'Active' : 'Blocked'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => toggleUserStatus(u.id, u.is_active !== false)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${u.is_active !== false ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'}`}
                  data-testid="toggle-user-status-btn"
                >
                  {u.is_active !== false ? 'Block User' : 'Unblock User'}
                </button>
              </div>

              <div className="grid grid-cols-4 gap-4 mt-4">
                {['free_credits', 'purchased_credits', 'referral_credits', 'total_credits'].map(field => (
                  <div key={field} className="bg-gray-800 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-400">{u[field] || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{field.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3 mt-4">
                {['free_credits', 'purchased_credits', 'referral_credits'].map(field => (
                  <div key={field} className="flex gap-2">
                    <input
                      type="number"
                      defaultValue={u[field] || 0}
                      className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm focus:border-amber-500 focus:outline-none"
                      data-testid={`edit-${field}`}
                      id={`edit-${field}`}
                    />
                    <button
                      onClick={() => updateCredits(u.id, field, document.getElementById(`edit-${field}`).value)}
                      className="px-2 py-1 bg-amber-600/30 text-amber-400 rounded text-xs hover:bg-amber-600/50"
                      data-testid={`save-${field}-btn`}
                    >
                      Set
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                <div><span className="text-gray-500">Referral code:</span> <span className="text-white font-mono">{u.referral_code}</span></div>
                <div><span className="text-gray-500">Referrals:</span> <span className="text-purple-400">{u.referral_count || 0}</span></div>
                <div><span className="text-gray-500">Sessions:</span> <span className="text-white">{userDetail.sessions_count}</span></div>
                <div><span className="text-gray-500">Registered:</span> <span className="text-gray-300">{u.created_at?.split('T')[0]}</span></div>
                <div><span className="text-gray-500">ID:</span> <span className="text-gray-400 font-mono text-xs">{u.id?.substring(0, 12)}...</span></div>
              </div>
            </div>

            {/* Recent transactions */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className="text-white font-medium mb-3">Recent Transactions ({userDetail.transactions?.length || 0})</h3>
              <div className="space-y-1 max-h-60 overflow-auto">
                {(userDetail.transactions || []).map((t, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-b border-gray-800/50">
                    <span className="text-gray-400">{t.action}</span>
                    <span className={t.amount < 0 ? 'text-red-400' : 'text-green-400'}>{t.amount > 0 ? '+' : ''}{t.amount}</span>
                    <span className="text-gray-600 text-xs">{t.created_at?.split('T')[0]}</span>
                  </div>
                ))}
                {(!userDetail.transactions || userDetail.transactions.length === 0) && <p className="text-gray-500 text-sm text-center py-4">No transactions</p>}
              </div>
            </div>

            {/* Referrals made */}
            {userDetail.referrals_made?.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-white font-medium mb-3">Referrals Made ({userDetail.referrals_made.length})</h3>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {userDetail.referrals_made.map((r, i) => (
                    <div key={i} className="flex justify-between text-sm py-1.5 border-b border-gray-800/50">
                      <span className="text-gray-400 font-mono text-xs">{r.referred_user_id?.substring(0, 12)}...</span>
                      <span className="text-green-400">+{r.credits}</span>
                      <span className="text-gray-600 text-xs">{r.created_at?.split('T')[0]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="users-tab">
      {/* Notifications banner */}
      {notifications.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="admin-notifications-banner">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-red-400 font-medium text-sm">Alerts ({notifications.length})</h3>
            <button onClick={markNotificationsRead} className="text-xs text-gray-400 hover:text-white">Mark all read</button>
          </div>
          {notifications.slice(0, 3).map((n, i) => (
            <div key={i} className="text-sm text-gray-300 py-1">
              {n.type === 'referral_abuse' && <span>Referral abuse: <span className="text-red-400">{n.referrer_email}</span> earned {n.total_credits_24h} credits in 24h</span>}
              {n.type === 'monthly_reset' && <span>Monthly reset: {n.users_affected} users credits zeroed</span>}
              <span className="text-gray-600 text-xs ml-2">{n.created_at?.split('T')[0]}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by email, name, id..."
            className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-gray-500 focus:border-amber-500 focus:outline-none text-sm"
            data-testid="users-search-input"
          />
        </div>
        <select value={authFilter} onChange={(e) => { setAuthFilter(e.target.value); setPage(1); }}
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:border-amber-500 focus:outline-none"
          data-testid="users-auth-filter"
        >
          <option value="">All providers</option>
          <option value="email">Email</option>
          <option value="google">Google</option>
        </select>
        <select value={verifiedFilter} onChange={(e) => { setVerifiedFilter(e.target.value); setPage(1); }}
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:border-amber-500 focus:outline-none"
          data-testid="users-verified-filter"
        >
          <option value="">All status</option>
          <option value="true">Verified</option>
          <option value="false">Unverified</option>
        </select>
        <select value={creditsFilter} onChange={(e) => { setCreditsFilter(e.target.value); setPage(1); }}
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:border-amber-500 focus:outline-none"
          data-testid="users-credits-filter"
        >
          <option value="">All credits</option>
          <option value="yes">Has credits</option>
          <option value="no">No credits</option>
        </select>
        <span className="text-gray-400 text-sm" data-testid="users-total-count">{total} users</span>
      </div>

      {/* Users table */}
      {loading ? (
        <div className="text-gray-500 text-center py-20"><SpinnerGap size={32} className="animate-spin mx-auto" /></div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400">
                <th className="text-left p-4 cursor-pointer hover:text-white" onClick={() => handleSort('email')}>User <SortIcon field="email" /></th>
                <th className="text-left p-4 cursor-pointer hover:text-white" onClick={() => handleSort('free_credits')}>Free <SortIcon field="free_credits" /></th>
                <th className="text-left p-4 cursor-pointer hover:text-white" onClick={() => handleSort('purchased_credits')}>Purchased <SortIcon field="purchased_credits" /></th>
                <th className="text-left p-4 cursor-pointer hover:text-white" onClick={() => handleSort('referral_credits')}>Referral <SortIcon field="referral_credits" /></th>
                <th className="text-left p-4">Provider</th>
                <th className="text-left p-4 cursor-pointer hover:text-white" onClick={() => handleSort('referral_count')}>Refs <SortIcon field="referral_count" /></th>
                <th className="text-left p-4 cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>Registered <SortIcon field="created_at" /></th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-gray-800/50 hover:bg-gray-800/30" data-testid={`user-row-${u.id}`}>
                  <td className="p-4">
                    <div>
                      <p className="text-white">{u.name || 'No name'}</p>
                      <p className="text-gray-500 text-xs">{u.email}</p>
                    </div>
                  </td>
                  <td className="p-4 text-amber-400">{u.free_credits || 0}</td>
                  <td className="p-4 text-green-400">{u.purchased_credits || 0}</td>
                  <td className="p-4 text-purple-400">{u.referral_credits || 0}</td>
                  <td className="p-4">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${u.auth_provider === 'google' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-400'}`}>
                      {u.auth_provider || 'email'}
                    </span>
                  </td>
                  <td className="p-4 text-gray-300">{u.referral_count || 0}</td>
                  <td className="p-4 text-gray-400 text-xs">{u.created_at?.split('T')[0]}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => loadUserDetail(u.id)}
                      className="text-amber-400 hover:text-amber-300 text-xs"
                      data-testid={`view-user-${u.id}`}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-8">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="users-pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 bg-gray-800 text-gray-400 rounded-lg text-sm disabled:opacity-30 hover:bg-gray-700"
          >
            Prev
          </button>
          <span className="text-gray-400 text-sm">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 bg-gray-800 text-gray-400 rounded-lg text-sm disabled:opacity-30 hover:bg-gray-700"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// AI Configuration Tab - Full AI Management
const AIConfigTab = ({ token }) => {
  const [config, setConfig] = useState(null);
  const [versions, setVersions] = useState([]);
  const [availableModels, setAvailableModels] = useState({});
  const [defaultPrompts, setDefaultPrompts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testMessage, setTestMessage] = useState('Привет, у меня проблемы с деньгами');
  const [activeSection, setActiveSection] = useState('models');
  
  // Edit state
  const [editConfig, setEditConfig] = useState({
    dialogue_model: { provider: 'anthropic', model: 'claude-sonnet-4-5-20250929' },
    advisor_model: { provider: 'openai', model: 'gpt-5.2' },
    email_model: { provider: 'openai', model: 'gpt-5.2' },
    divination_model: { provider: 'openai', model: 'gpt-5.2' },
    core_prompt: '',
    advisor_prompt: '',
    topic_prompts: {},
    topic_models: {},
  });

  const headers = { Authorization: `Bearer ${token}` };

  const loadConfig = useCallback(async () => {
    try {
      const [configRes, versionsRes] = await Promise.all([
        axios.get(`${API}/admin/ai-config`, { headers }),
        axios.get(`${API}/admin/ai-config/versions`, { headers }),
      ]);
      
      setConfig(configRes.data.config);
      setAvailableModels(configRes.data.available_models);
      setDefaultPrompts(configRes.data.default_prompts);
      setVersions(versionsRes.data.versions);
      
      // Initialize edit state from config
      if (configRes.data.config) {
        setEditConfig({
          dialogue_model: configRes.data.config.dialogue_model || { provider: 'anthropic', model: 'claude-sonnet-4-5-20250929' },
          advisor_model: configRes.data.config.advisor_model || { provider: 'openai', model: 'gpt-5.2' },
          email_model: configRes.data.config.email_model || { provider: 'openai', model: 'gpt-5.2' },
          divination_model: configRes.data.config.divination_model || { provider: 'openai', model: 'gpt-5.2' },
          core_prompt: configRes.data.config.core_prompt || configRes.data.default_prompts.core_prompt || '',
          advisor_prompt: configRes.data.config.advisor_prompt || configRes.data.default_prompts.advisor_prompt || '',
          topic_prompts: configRes.data.config.topic_prompts || configRes.data.default_prompts.topic_prompts || {},
          topic_models: configRes.data.config.topic_models || {},
        });
      }
    } catch (err) {
      void 0;
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ai-config`, editConfig, { headers });
      await loadConfig();
      alert('Configuration saved as new version!');
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const activateVersion = async (version) => {
    if (!window.confirm(`Activate version ${version}? This will immediately affect all users.`)) return;
    try {
      await axios.post(`${API}/admin/ai-config/${version}/activate`, {}, { headers });
      await loadConfig();
      alert(`Version ${version} activated!`);
    } catch (err) {
      alert('Failed to activate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const testConfig = async (version = null) => {
    setTesting(true);
    setTestResult(null);
    try {
      const targetVersion = version || (versions[0]?.version) || 0;
      const res = await axios.post(`${API}/admin/ai-config/${targetVersion}/test`, 
        { message: testMessage }, 
        { headers }
      );
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || err.message });
    } finally {
      setTesting(false);
    }
  };

  const updateModel = (task, field, value) => {
    setEditConfig(prev => ({
      ...prev,
      [`${task}_model`]: { ...prev[`${task}_model`], [field]: value }
    }));
  };

  const updateTopicPrompt = (topicId, value) => {
    setEditConfig(prev => ({
      ...prev,
      topic_prompts: { ...prev.topic_prompts, [topicId]: value }
    }));
  };

  const countTokens = (text) => Math.ceil((text || '').length / 4);

  if (loading) return <div className="text-gray-500 text-center py-20"><SpinnerGap size={32} className="animate-spin mx-auto" /></div>;

  const topics = ['money', 'love', 'health', 'tragedy', 'business', 'growth'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl text-white font-medium">AI Configuration</h2>
          <p className="text-gray-500 text-sm mt-1">
            Current version: <span className="text-amber-400">{config?.version || 0}</span>
          </p>
        </div>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          data-testid="save-ai-config-btn"
        >
          {saving ? <SpinnerGap size={16} className="animate-spin" /> : 'Save as New Version'}
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-800 pb-3">
        {[
          { id: 'models', label: 'Models' },
          { id: 'prompts', label: 'Core Prompts' },
          { id: 'topics', label: 'Topic Prompts' },
          { id: 'test', label: 'Test' },
          { id: 'versions', label: 'Version History' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              activeSection === tab.id 
                ? 'bg-amber-600/20 text-amber-400' 
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
            data-testid={`ai-section-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Models Section */}
      {activeSection === 'models' && (
        <div className="space-y-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-white font-medium mb-4">Model Selection by Task</h3>
            <div className="grid grid-cols-2 gap-6">
              {[
                { key: 'dialogue', label: 'Main Dialogue (ONIMA)', desc: 'Responds to users in chat' },
                { key: 'advisor', label: 'Hidden Advisor', desc: 'Analyzes conversation, gives hints' },
                { key: 'email', label: 'Emails & Rituals', desc: 'Generates ritual documents' },
                { key: 'divination', label: 'Divinations', desc: 'Tarot, Horoscope, Numerology' },
              ].map((task) => (
                <div key={task.key} className="space-y-3">
                  <div>
                    <p className="text-white text-sm font-medium">{task.label}</p>
                    <p className="text-gray-500 text-xs">{task.desc}</p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={editConfig[`${task.key}_model`]?.provider || 'anthropic'}
                      onChange={(e) => updateModel(task.key, 'provider', e.target.value)}
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                      data-testid={`model-provider-${task.key}`}
                    >
                      {Object.keys(availableModels).map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <select
                      value={editConfig[`${task.key}_model`]?.model || ''}
                      onChange={(e) => updateModel(task.key, 'model', e.target.value)}
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                      data-testid={`model-name-${task.key}`}
                    >
                      {(availableModels[editConfig[`${task.key}_model`]?.provider] || []).map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Core Prompts Section */}
      {activeSection === 'prompts' && (
        <div className="space-y-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-medium">ONIMA Core Personality</h3>
              <span className="text-gray-500 text-xs">~{countTokens(editConfig.core_prompt)} tokens</span>
            </div>
            <textarea
              value={editConfig.core_prompt}
              onChange={(e) => setEditConfig(prev => ({ ...prev, core_prompt: e.target.value }))}
              rows={20}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm font-mono focus:border-amber-500 focus:outline-none resize-y"
              placeholder="Enter core personality prompt..."
              data-testid="core-prompt-textarea"
            />
            <button
              onClick={() => setEditConfig(prev => ({ ...prev, core_prompt: defaultPrompts.core_prompt }))}
              className="mt-2 text-amber-400 text-xs hover:underline"
            >
              Reset to default
            </button>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-medium">Advisor Prompt (Hidden)</h3>
              <span className="text-gray-500 text-xs">~{countTokens(editConfig.advisor_prompt)} tokens</span>
            </div>
            <textarea
              value={editConfig.advisor_prompt}
              onChange={(e) => setEditConfig(prev => ({ ...prev, advisor_prompt: e.target.value }))}
              rows={15}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm font-mono focus:border-amber-500 focus:outline-none resize-y"
              placeholder="Enter advisor prompt..."
              data-testid="advisor-prompt-textarea"
            />
            <button
              onClick={() => setEditConfig(prev => ({ ...prev, advisor_prompt: defaultPrompts.advisor_prompt }))}
              className="mt-2 text-amber-400 text-xs hover:underline"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}

      {/* Topic Prompts Section */}
      {activeSection === 'topics' && (
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">Topic-specific behavior instructions for ONIMA</p>
          {topics.map((topic) => (
            <div key={topic} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium capitalize">{topic}</h3>
                <span className="text-gray-500 text-xs">~{countTokens(editConfig.topic_prompts?.[topic])} tokens</span>
              </div>
              <textarea
                value={editConfig.topic_prompts?.[topic] || ''}
                onChange={(e) => updateTopicPrompt(topic, e.target.value)}
                rows={4}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm font-mono focus:border-amber-500 focus:outline-none resize-y"
                placeholder={`Enter ${topic} mode prompt...`}
                data-testid={`topic-prompt-${topic}`}
              />
              <button
                onClick={() => updateTopicPrompt(topic, defaultPrompts.topic_prompts?.[topic] || '')}
                className="mt-1 text-amber-400 text-xs hover:underline"
              >
                Reset to default
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Test Section */}
      {activeSection === 'test' && (
        <div className="space-y-6">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-white font-medium mb-4">Test AI Response</h3>
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Test Message</label>
                <input
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-amber-500 focus:outline-none"
                  placeholder="Enter test message..."
                  data-testid="test-message-input"
                />
              </div>
              <button
                onClick={() => testConfig()}
                disabled={testing}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                data-testid="test-ai-btn"
              >
                {testing ? <SpinnerGap size={16} className="animate-spin" /> : 'Test Current Config'}
              </button>
            </div>
          </div>

          {testResult && (
            <div className={`bg-gray-900 border rounded-xl p-6 ${testResult.success ? 'border-green-500/30' : 'border-red-500/30'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-medium ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                  {testResult.success ? 'Test Successful' : 'Test Failed'}
                </h3>
                <span className="text-gray-500 text-xs">{testResult.model_used}</span>
              </div>
              {testResult.success ? (
                <div>
                  <p className="text-gray-400 text-sm mb-2">User: {testResult.test_message}</p>
                  <p className="text-white bg-gray-800 rounded-lg p-4">{testResult.response}</p>
                </div>
              ) : (
                <p className="text-red-400">{testResult.error}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Version History Section */}
      {activeSection === 'versions' && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400">
                <th className="text-left p-4">Version</th>
                <th className="text-left p-4">Dialogue Model</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Created</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="p-4 text-white font-medium">v{v.version}</td>
                  <td className="p-4 text-gray-400">
                    {v.dialogue_model?.provider}/{v.dialogue_model?.model}
                  </td>
                  <td className="p-4">
                    {v.is_active ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">Active</span>
                    ) : v.is_draft ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">Draft</span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400">Inactive</span>
                    )}
                  </td>
                  <td className="p-4 text-gray-500">
                    {new Date(v.created_at).toLocaleString()}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => testConfig(v.version)}
                        className="text-purple-400 hover:text-purple-300 text-xs"
                        data-testid={`test-version-${v.version}`}
                      >
                        Test
                      </button>
                      {!v.is_active && (
                        <button
                          onClick={() => activateVersion(v.version)}
                          className="text-amber-400 hover:text-amber-300 text-xs"
                          data-testid={`activate-version-${v.version}`}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {versions.length === 0 && (
                <tr><td colSpan={5} className="text-center text-gray-500 py-8">No versions yet. Save your first configuration.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Telegram Management Tab
const TelegramTab = ({ token }) => {
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [previewText, setPreviewText] = useState('');
  const [previewType, setPreviewType] = useState('card_of_day');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const postTypes = [
    { id: 'card_of_day', label: 'Карта дня', icon: '🃏' },
    { id: 'interactive', label: 'Інтерактив', icon: '🔮' },
    { id: 'evening', label: 'Вечірній', icon: '🌙' },
    { id: 'night', label: 'Нічний', icon: '🌑' },
    { id: 'esoteric_fact', label: 'Езо-факт', icon: '✨' },
    { id: 'affirmation', label: 'Афірмація', icon: '💫' },
    { id: 'video', label: 'Відео', icon: '🎬' },
    { id: 'night_video', label: 'Нічне відео', icon: '🌌' },
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, scheduleRes, healthRes] = await Promise.all([
        axios.get(`${API}/telegram/scheduler-status`),
        axios.get(`${API}/telegram/schedule`),
        axios.get(`${API}/telegram/health`),
      ]);
      setSchedulerStatus(statusRes.data);
      setRecentPosts(scheduleRes.data.recent_posts || []);
      setHealthStatus(healthRes.data);
    } catch (err) {
      void 0;
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePreview = async (type) => {
    setPreviewType(type);
    try {
      const res = await axios.get(`${API}/telegram/preview/${type}`);
      setPreviewText(res.data.text);
    } catch (err) {
      setPreviewText('Error loading preview');
    }
  };

  const handleSend = async (type) => {
    setSending(true);
    try {
      await axios.post(`${API}/telegram/post`, { post_type: type });
      loadData();
    } catch (err) {
      void 0;
    }
    setSending(false);
  };

  if (loading) return <div className="text-gray-400 text-center py-12"><SpinnerGap size={32} className="animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Telegram Auto-Poster</h2>

      {/* Health Monitor */}
      <div className={`border rounded-xl p-5 ${healthStatus?.status === 'ALERT' ? 'bg-red-950 border-red-700' : 'bg-gray-900 border-gray-800'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium flex items-center gap-2">
            <span className="text-lg">🏥</span> Health Monitor
          </h3>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${healthStatus?.status === 'OK' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/30 text-red-400 animate-pulse'}`}>
            {healthStatus?.status || '...'}
          </span>
        </div>
        {healthStatus?.status === 'ALERT' && (
          <div className="bg-red-900/50 border border-red-600 rounded-lg p-3 mb-4 text-red-300 text-sm">
            ⚠️ {healthStatus.alert_reason}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-amber-400">{healthStatus?.posts_today ?? '-'}</div>
            <div className="text-gray-400 text-xs mt-1">Постів сьогодні</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-amber-400">{healthStatus?.recent_posts_24h ?? '-'}</div>
            <div className="text-gray-400 text-xs mt-1">За 24 год</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="text-2xl font-bold text-blue-400">{healthStatus?.hours_since_last_post != null ? `${healthStatus.hours_since_last_post}h` : '-'}</div>
            <div className="text-gray-400 text-xs mt-1">Від останнього</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className={`text-2xl font-bold ${healthStatus?.is_working_hours ? 'text-green-400' : 'text-gray-500'}`}>
              {healthStatus?.current_ua_hour ?? '-'}:00
            </div>
            <div className="text-gray-400 text-xs mt-1">{healthStatus?.is_working_hours ? 'Робочий час' : 'Ніч'}</div>
          </div>
        </div>
        {healthStatus?.posts_by_type_today && Object.keys(healthStatus.posts_by_type_today).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(healthStatus.posts_by_type_today).map(([type, count]) => (
              <span key={type} className="px-2 py-1 rounded-full bg-gray-700 text-gray-300 text-xs">
                {type}: {count}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Scheduler Status */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Clock size={20} className="text-amber-400" /> Scheduler Status
          </h3>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${schedulerStatus?.scheduler_running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {schedulerStatus?.scheduler_running ? 'Running' : 'Stopped'}
          </span>
        </div>
        <p className="text-gray-400 text-sm mb-3">Current UA time: {schedulerStatus?.current_ua_hour}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {schedulerStatus?.upcoming_posts?.map((p, i) => (
            <div key={i} className="bg-gray-800 rounded-lg p-2 text-center">
              <div className="text-amber-400 text-sm font-mono">{p.time_ua}</div>
              <div className="text-gray-400 text-xs mt-0.5">{p.type}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Manual Post */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <PaperPlaneTilt size={20} className="text-amber-400" /> Manual Post
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {postTypes.map((pt) => (
            <button
              key={pt.id}
              onClick={() => handlePreview(pt.id)}
              className={`p-3 rounded-lg text-sm transition-colors border ${previewType === pt.id ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'}`}
              data-testid={`tg-preview-${pt.id}`}
            >
              <span className="text-lg">{pt.icon}</span>
              <div className="mt-1 text-xs">{pt.label}</div>
            </button>
          ))}
        </div>
        {previewText && (
          <div className="bg-gray-800 rounded-lg p-4 mb-4">
            <p className="text-xs text-gray-500 mb-2">Preview:</p>
            <pre className="text-gray-300 text-sm whitespace-pre-wrap font-sans">{previewText}</pre>
          </div>
        )}
        <button
          onClick={() => handleSend(previewType)}
          disabled={sending || !previewText}
          className="px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          data-testid="tg-send-btn"
        >
          {sending ? <SpinnerGap size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
          Send to Channel
        </button>
      </div>

      {/* Recent Posts */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-white font-medium mb-4">Recent Posts ({recentPosts.length})</h3>
        <div className="space-y-2 max-h-96 overflow-auto">
          {recentPosts.map((post, i) => (
            <div key={i} className="bg-gray-800 rounded-lg p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-amber-400 text-xs font-medium">{post.post_type}</span>
                  {post.is_video && <FilmSlate size={14} className="text-purple-400" />}
                  {post.scheduled && <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 rounded">auto</span>}
                </div>
                <p className="text-gray-400 text-xs line-clamp-2">{post.text?.substring(0, 120)}...</p>
                <p className="text-gray-600 text-[10px] mt-1">{post.created_at}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${post.result?.ok ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {post.result?.ok ? 'sent' : 'fail'}
              </span>
            </div>
          ))}
          {recentPosts.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No posts yet</p>}
        </div>
      </div>
    </div>
  );
};

// ============== SECURITY TAB ==============
const SecurityTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [blocklist, setBlocklist] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [blockForm, setBlockForm] = useState({ ip: '', reason: '', hours: 24 });
  const [isPermanent, setIsPermanent] = useState(false);
  const [filterRisk, setFilterRisk] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [page, setPage] = useState(1);
  const [emergencyMode, setEmergencyMode] = useState({ registration_disabled: false, referrals_disabled: false, lockdown: false });
  const [emergencyLoading, setEmergencyLoading] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/security/stats`, { headers });
      setStats(res.data);
      if (res.data.emergency_mode) setEmergencyMode(res.data.emergency_mode);
    } catch (e) { /* silent */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/security/events?page=${page}&per_page=30&min_risk=${filterRisk}`, { headers });
      setEvents(res.data.events || []);
      setTotalEvents(res.data.total || 0);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [token, page, filterRisk]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBlocklist = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/security/blocklist`, { headers });
      setBlocklist(res.data.blocked || []);
    } catch (e) { /* silent */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadStats();
    if (activeSection === 'events') loadEvents();
    if (activeSection === 'blocklist') loadBlocklist();
  }, [activeSection, loadStats, loadEvents, loadBlocklist]);

  const handleBlockIP = async (e) => {
    e.preventDefault();
    try {
      const payload = { ip: blockForm.ip, reason: blockForm.reason, hours: isPermanent ? null : blockForm.hours };
      await axios.post(`${API}/admin/security/block-ip`, payload, { headers });
      setBlockForm({ ip: '', reason: '', hours: 24 });
      setIsPermanent(false);
      loadBlocklist();
      loadStats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to block IP');
    }
  };

  const toggleEmergency = async (flag) => {
    setEmergencyLoading(flag);
    try {
      const res = await axios.post(`${API}/admin/security/emergency-mode`, 
        { [flag]: !emergencyMode[flag] }, { headers });
      setEmergencyMode(res.data.emergency_mode);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to toggle emergency mode');
    }
    setEmergencyLoading(null);
  };

  const handleUnblock = async (ip) => {
    if (!window.confirm(`Unblock ${ip}?`)) return;
    await axios.delete(`${API}/admin/security/unblock-ip/${ip}`, { headers });
    loadBlocklist();
    loadStats();
  };

  const getRiskColor = (score) => {
    if (score >= 0.7) return 'text-red-400';
    if (score >= 0.4) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getActionBadge = (action) => {
    if (action === 'soft_block') return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (action === 'warn') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-green-500/20 text-green-400 border-green-500/30';
  };

  return (
    <div className="space-y-6" data-testid="security-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ShieldWarning size={24} className="text-amber-400" />
            Security Center
          </h2>
          <p className="text-gray-500 text-sm mt-1">Behavioral analysis, threat detection & IP management</p>
        </div>
        <button onClick={() => { loadStats(); loadEvents(); loadBlocklist(); }}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors">
          Refresh
        </button>
      </div>

      {/* Emergency Mode Panel (Kill Switch) */}
      <div className={`rounded-xl p-5 border ${
        emergencyMode.lockdown ? 'bg-red-950/50 border-red-500 animate-pulse' : 
        (emergencyMode.registration_disabled || emergencyMode.referrals_disabled) ? 'bg-amber-950/30 border-amber-500/50' : 
        'bg-gray-900 border-gray-800'
      }`} data-testid="emergency-mode-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            {emergencyMode.lockdown ? <ShieldSlash size={18} className="text-red-400" /> : <Lock size={18} className="text-amber-400" />}
            {emergencyMode.lockdown ? 'LOCKDOWN ACTIVE' : 'Emergency Controls'}
          </h3>
          {(emergencyMode.lockdown || emergencyMode.registration_disabled || emergencyMode.referrals_disabled) && (
            <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 font-medium">Active</span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Disable Registration */}
          <button
            onClick={() => toggleEmergency('registration_disabled')}
            disabled={emergencyLoading === 'registration_disabled'}
            data-testid="toggle-registration"
            className={`flex items-center justify-between px-4 py-3 rounded-lg border text-sm transition-all ${
              emergencyMode.registration_disabled 
                ? 'bg-red-500/20 border-red-500/50 text-red-300' 
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-red-500/30 hover:text-gray-200'
            }`}>
            <div className="flex items-center gap-2">
              <ShieldSlash size={16} />
              <span>Registration</span>
            </div>
            <span className={`text-xs font-bold ${emergencyMode.registration_disabled ? 'text-red-400' : 'text-green-400'}`}>
              {emergencyMode.registration_disabled ? 'BLOCKED' : 'OPEN'}
            </span>
          </button>
          {/* Disable Referrals */}
          <button
            onClick={() => toggleEmergency('referrals_disabled')}
            disabled={emergencyLoading === 'referrals_disabled'}
            data-testid="toggle-referrals"
            className={`flex items-center justify-between px-4 py-3 rounded-lg border text-sm transition-all ${
              emergencyMode.referrals_disabled 
                ? 'bg-orange-500/20 border-orange-500/50 text-orange-300' 
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-orange-500/30 hover:text-gray-200'
            }`}>
            <div className="flex items-center gap-2">
              <Warning size={16} />
              <span>Referrals</span>
            </div>
            <span className={`text-xs font-bold ${emergencyMode.referrals_disabled ? 'text-orange-400' : 'text-green-400'}`}>
              {emergencyMode.referrals_disabled ? 'BLOCKED' : 'OPEN'}
            </span>
          </button>
          {/* Full Lockdown */}
          <button
            onClick={() => {
              if (!emergencyMode.lockdown && !window.confirm('FULL LOCKDOWN blocks registration + referrals + all new sessions. Continue?')) return;
              toggleEmergency('lockdown');
            }}
            disabled={emergencyLoading === 'lockdown'}
            data-testid="toggle-lockdown"
            className={`flex items-center justify-between px-4 py-3 rounded-lg border text-sm transition-all ${
              emergencyMode.lockdown 
                ? 'bg-red-600/30 border-red-500 text-red-200' 
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-red-500/30 hover:text-gray-200'
            }`}>
            <div className="flex items-center gap-2">
              <Lock size={16} />
              <span>Full Lockdown</span>
            </div>
            <span className={`text-xs font-bold ${emergencyMode.lockdown ? 'text-red-400' : 'text-green-400'}`}>
              {emergencyMode.lockdown ? 'ACTIVE' : 'OFF'}
            </span>
          </button>
        </div>
        {emergencyMode.lockdown && (
          <p className="text-red-400/70 text-xs mt-3">All new registrations, referrals, and anonymous access are blocked. Only existing verified users can log in.</p>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid="security-events-24h">
            <div className="flex items-center gap-2 mb-2"><Warning size={18} className="text-amber-400" /><span className="text-gray-400 text-sm">Events 24h</span></div>
            <p className="text-2xl font-bold text-white">{stats.events_24h}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid="security-blocked-24h">
            <div className="flex items-center gap-2 mb-2"><ShieldSlash size={18} className="text-red-400" /><span className="text-gray-400 text-sm">Blocked 24h</span></div>
            <p className="text-2xl font-bold text-red-400">{stats.blocked_24h}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid="security-warnings-24h">
            <div className="flex items-center gap-2 mb-2"><ShieldWarning size={18} className="text-yellow-400" /><span className="text-gray-400 text-sm">Warnings 24h</span></div>
            <p className="text-2xl font-bold text-yellow-400">{stats.warnings_24h}</p>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid="security-active-blocks">
            <div className="flex items-center gap-2 mb-2"><Lock size={18} className="text-orange-400" /><span className="text-gray-400 text-sm">Active Blocks</span></div>
            <p className="text-2xl font-bold text-orange-400">{stats.active_blocks}</p>
          </div>
        </div>
      )}

      {/* Risky IPs */}
      {stats?.risky_ips?.length > 0 && (
        <div className="bg-gray-900 border border-amber-500/20 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
            <ShieldWarning size={16} /> Top Risk IPs (7 days)
          </h3>
          <div className="space-y-2">
            {stats.risky_ips.map((r, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-2">
                <span className="text-gray-300 font-mono text-sm">{r.ip}</span>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500 text-xs">{r.events} events</span>
                  <span className={`text-sm font-bold ${getRiskColor(r.max_score)}`}>{(r.max_score * 100).toFixed(0)}%</span>
                  <button onClick={() => setBlockForm(p => ({...p, ip: r.ip}))}
                    className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded-lg transition-colors">
                    Block
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section Nav */}
      <div className="flex gap-2 border-b border-gray-800 pb-0">
        {['overview', 'events', 'blocklist'].map(s => (
          <button key={s} onClick={() => setActiveSection(s)}
            className={`px-4 py-2 text-sm rounded-t-lg transition-colors capitalize ${activeSection === s ? 'bg-gray-800 text-amber-400 border-b-2 border-amber-400' : 'text-gray-500 hover:text-gray-300'}`}>
            {s === 'blocklist' ? 'IP Blocklist' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Events */}
      {activeSection === 'events' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-gray-400 text-sm">Min risk:</label>
            <select value={filterRisk} onChange={e => { setFilterRisk(parseFloat(e.target.value)); setPage(1); }}
              className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-1.5 text-sm">
              <option value={0}>All</option>
              <option value={0.4}>Warn (≥0.4)</option>
              <option value={0.7}>Block (≥0.7)</option>
            </select>
            <span className="text-gray-500 text-sm">Total: {totalEvents}</span>
          </div>
          {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-gray-500 text-xs uppercase tracking-wider">
                  <th className="text-left pb-3">Time</th>
                  <th className="text-left pb-3">Type</th>
                  <th className="text-left pb-3">IP</th>
                  <th className="text-left pb-3">Risk</th>
                  <th className="text-left pb-3">Action</th>
                  <th className="text-left pb-3">Reasons</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-800/50">
                  {events.map((e, i) => (
                    <tr key={i} className="text-gray-300 hover:bg-gray-800/30">
                      <td className="py-2.5 text-gray-500 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="py-2.5 font-mono text-xs">{e.event_type}</td>
                      <td className="py-2.5 font-mono text-xs">{e.ip}</td>
                      <td className="py-2.5"><span className={`font-bold ${getRiskColor(e.risk_score)}`}>{(e.risk_score * 100).toFixed(0)}%</span></td>
                      <td className="py-2.5"><span className={`px-2 py-0.5 text-xs rounded border ${getActionBadge(e.action_taken)}`}>{e.action_taken}</span></td>
                      <td className="py-2.5 text-xs text-gray-500 max-w-xs truncate">{(e.reasons || []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalEvents > 30 && (
                <div className="flex gap-2 mt-4 justify-center">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 bg-gray-800 text-gray-300 rounded disabled:opacity-40">Prev</button>
                  <span className="px-3 py-1 text-gray-500 text-sm">Page {page}</span>
                  <button disabled={page * 30 >= totalEvents} onClick={() => setPage(p => p + 1)} className="px-3 py-1 bg-gray-800 text-gray-300 rounded disabled:opacity-40">Next</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* IP Blocklist */}
      {activeSection === 'blocklist' && (
        <div className="space-y-5">
          {/* Add block form */}
          <form onSubmit={handleBlockIP} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Block IP Manually</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input value={blockForm.ip} onChange={e => setBlockForm(p => ({...p, ip: e.target.value}))}
                placeholder="IP address" required
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                data-testid="block-ip-input" />
              <input value={blockForm.reason} onChange={e => setBlockForm(p => ({...p, reason: e.target.value}))}
                placeholder="Reason" required
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none" />
              <div className="flex gap-2 items-center">
                {isPermanent ? (
                  <div className="flex-1 bg-red-900/30 border border-red-500/50 rounded-lg px-3 py-2 text-red-400 text-sm font-bold text-center">
                    PERMANENT
                  </div>
                ) : (
                  <select value={blockForm.hours} onChange={e => setBlockForm(p => ({...p, hours: parseInt(e.target.value)}))}
                    className="flex-1 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm">
                    <option value={1}>1h</option>
                    <option value={6}>6h</option>
                    <option value={24}>24h</option>
                    <option value={168}>7d</option>
                    <option value={720}>30d</option>
                  </select>
                )}
                <button type="button" onClick={() => setIsPermanent(!isPermanent)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    isPermanent ? 'bg-red-600 text-white' : 'bg-gray-800 border border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-500/30'
                  }`} data-testid="toggle-permanent-block">
                  {isPermanent ? <Lock size={14} /> : <Lock size={14} />}
                </button>
              </div>
              <button type="submit" className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm transition-colors font-medium" data-testid="submit-block-ip">
                {isPermanent ? 'Block Forever' : 'Block'}
              </button>
            </div>
          </form>

          {/* Blocked IPs list */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-800">
              <span className="text-sm text-gray-400">{blocklist.length} active blocks</span>
            </div>
            {blocklist.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                <ShieldCheck size={20} className="text-green-400" /> No IPs currently blocked
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {blocklist.map((b, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <span className="text-white font-mono text-sm">{b.ip}</span>
                      <p className="text-gray-500 text-xs mt-0.5">{b.reason}</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-gray-500">
                        {b.expires_at ? `Until ${new Date(b.expires_at).toLocaleDateString()}` : 'Permanent'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${b.created_by === 'admin' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
                        {b.created_by}
                      </span>
                      <button onClick={() => handleUnblock(b.ip)}
                        className="px-3 py-1 bg-gray-800 hover:bg-green-500/20 text-gray-400 hover:text-green-400 rounded-lg transition-colors"
                        data-testid={`unblock-${b.ip}`}>
                        Unblock
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// Archetypes Tab (stub)
const ArchetypesTab = () => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
    <Gear size={48} className="text-gray-600 mx-auto mb-4" />
    <p className="text-gray-500">Use the AI Configuration tab instead.</p>
  </div>
);

// ======== SEO GUIDES TAB ========
const SEOGuidesTab = ({ token }) => {
  const [guides, setGuides] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterLang, setFilterLang] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingGuide, setEditingGuide] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const languages = ['en', 'de', 'ru', 'uk'];
  const categories = ['tarot', 'horoscope', 'numerology', 'astrology'];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterLang !== 'all') params.append('lang', filterLang);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const [guidesRes, statsRes] = await Promise.all([
        axios.get(`${API}/guides/admin/list?${params}`),
        axios.get(`${API}/guides/stats`)
      ]);
      setGuides(guidesRes.data.guides || []);
      setStats(statsRes.data);
    } catch (err) {
      void 0;
    }
    setLoading(false);
  }, [filterLang, filterCategory, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePublish = async (guideId) => {
    try {
      await axios.post(`${API}/guides/admin/${guideId}/publish`);
      loadData();
    } catch (err) {
      alert('Failed to publish');
    }
  };

  const handleUnpublish = async (guideId) => {
    try {
      await axios.post(`${API}/guides/admin/${guideId}/unpublish`);
      loadData();
    } catch (err) {
      alert('Failed to unpublish');
    }
  };

  const handleDelete = async (guideId) => {
    if (!window.confirm('Delete this guide permanently?')) return;
    try {
      await axios.delete(`${API}/guides/admin/${guideId}`);
      loadData();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">SEO Guide Pages</h2>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium transition-colors"
          data-testid="create-guide-btn"
        >
          <Plus size={18} />
          New Guide
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-amber-400">{stats.total}</div>
            <div className="text-gray-500 text-xs mt-1">Total</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{stats.published}</div>
            <div className="text-gray-500 text-xs mt-1">Published</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-yellow-400">{stats.draft}</div>
            <div className="text-gray-500 text-xs mt-1">Drafts</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="flex gap-2 justify-center mb-1">
              {Object.entries(stats.by_language || {}).map(([lang, count]) => (
                <span key={lang} className="text-xs text-gray-400">{lang.toUpperCase()}: {count}</span>
              ))}
            </div>
            <div className="text-gray-500 text-xs text-center">By Language</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-gray-900 border border-gray-800 rounded-xl p-4">
        <select
          value={filterLang}
          onChange={(e) => setFilterLang(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="filter-lang"
        >
          <option value="all">All Languages</option>
          {languages.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="filter-category"
        >
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="filter-status"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {/* Guides List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <SpinnerGap size={32} className="animate-spin text-amber-500" />
        </div>
      ) : guides.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <Article size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No guides found. Create your first SEO guide!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {guides.map((guide) => (
            <div
              key={guide.id}
              className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center justify-between gap-4"
              data-testid={`guide-item-${guide.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    guide.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {guide.status}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">{guide.lang?.toUpperCase()}</span>
                  <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">{guide.category}</span>
                </div>
                <h3 className="text-white font-medium truncate">{guide.title}</h3>
                <p className="text-gray-500 text-sm truncate">/guide/{guide.lang}/{guide.category}/{guide.slug}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={`/guide/${guide.lang}/${guide.category}/${guide.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
                  title="Preview"
                >
                  <Eye size={18} />
                </a>
                <button
                  onClick={() => setEditingGuide(guide)}
                  className="p-2 text-gray-400 hover:text-amber-400 rounded-lg hover:bg-gray-800 transition-colors"
                  title="Edit"
                >
                  <PencilSimple size={18} />
                </button>
                {guide.status === 'draft' ? (
                  <button
                    onClick={() => handlePublish(guide.id)}
                    className="p-2 text-gray-400 hover:text-green-400 rounded-lg hover:bg-gray-800 transition-colors"
                    title="Publish"
                  >
                    <Check size={18} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleUnpublish(guide.id)}
                    className="p-2 text-gray-400 hover:text-yellow-400 rounded-lg hover:bg-gray-800 transition-colors"
                    title="Unpublish"
                  >
                    <X size={18} />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(guide.id)}
                  className="p-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-gray-800 transition-colors"
                  title="Delete"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(isCreating || editingGuide) && (
        <GuideEditor
          guide={editingGuide}
          onClose={() => { setIsCreating(false); setEditingGuide(null); }}
          onSaved={() => { setIsCreating(false); setEditingGuide(null); loadData(); }}
        />
      )}
    </div>
  );
};

// Guide Editor Modal
const GuideEditor = ({ guide, onClose, onSaved }) => {
  const [form, setForm] = useState({
    lang: guide?.lang || 'de',
    category: guide?.category || 'tarot',
    slug: guide?.slug || '',
    title: guide?.title || '',
    meta_title: guide?.meta_title || '',
    meta_description: guide?.meta_description || '',
    og_image: guide?.og_image || '',
    hook: guide?.hook || '',
    sections: guide?.sections || [{ heading: '', content: '', bullets: [] }],
    faq: guide?.faq || [],
    tags: guide?.tags || [],
    alternates: guide?.alternates || [],
    status: guide?.status || 'draft'
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [tagsInput, setTagsInput] = useState((guide?.tags || []).join(', '));

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSectionChange = (idx, field, value) => {
    const newSections = [...form.sections];
    newSections[idx] = { ...newSections[idx], [field]: value };
    setForm(prev => ({ ...prev, sections: newSections }));
  };

  const addSection = () => {
    setForm(prev => ({
      ...prev,
      sections: [...prev.sections, { heading: '', content: '', bullets: [] }]
    }));
    setActiveSection(form.sections.length);
  };

  const removeSection = (idx) => {
    if (form.sections.length <= 1) return;
    setForm(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== idx)
    }));
    setActiveSection(Math.max(0, activeSection - 1));
  };

  const addFaq = () => {
    setForm(prev => ({
      ...prev,
      faq: [...prev.faq, { question: '', answer: '' }]
    }));
  };

  const handleFaqChange = (idx, field, value) => {
    const newFaq = [...form.faq];
    newFaq[idx] = { ...newFaq[idx], [field]: value };
    setForm(prev => ({ ...prev, faq: newFaq }));
  };

  const removeFaq = (idx) => {
    setForm(prev => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== idx)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...form,
        tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (guide?.id) {
        await axios.put(`${API}/guides/admin/${guide.id}`, data);
      } else {
        await axios.post(`${API}/guides/admin/create`, data);
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-bold text-white">
            {guide ? 'Edit Guide' : 'Create New Guide'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Language</label>
              <select
                value={form.lang}
                onChange={(e) => handleChange('lang', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                disabled={!!guide}
              >
                <option value="de">DE</option>
                <option value="en">EN</option>
                <option value="ru">RU</option>
                <option value="uk">UK</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                disabled={!!guide}
              >
                <option value="tarot">Tarot</option>
                <option value="horoscope">Horoscope</option>
                <option value="numerology">Numerology</option>
                <option value="astrology">Astrology</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Slug (URL)</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => handleChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="tower"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                disabled={!!guide}
              />
            </div>
          </div>

          {/* SEO Meta */}
          <div className="space-y-4">
            <h4 className="text-white font-medium flex items-center gap-2">
              <Globe size={18} className="text-amber-400" />
              SEO Meta
            </h4>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Title (H1)</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="The Tower – Tarot Meaning"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Meta Title (50-60 chars)</label>
              <input
                type="text"
                value={form.meta_title}
                onChange={(e) => handleChange('meta_title', e.target.value)}
                placeholder="The Tower – Tarot Meaning: Destruction as New Beginning | ONIMA"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">{form.meta_title.length}/60 characters</p>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Meta Description (150-160 chars)</label>
              <textarea
                value={form.meta_description}
                onChange={(e) => handleChange('meta_description', e.target.value)}
                placeholder="What collapses makes space. The Tower in Tarot: meaning in love, career & life."
                rows={2}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">{form.meta_description.length}/160 characters</p>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">OG Image URL (optional)</label>
              <input
                type="text"
                value={form.og_image}
                onChange={(e) => handleChange('og_image', e.target.value)}
                placeholder="https://onima.cc/images/tarot/tower.jpg"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
          </div>

          {/* Hook */}
          <div>
            <label className="block text-gray-400 text-sm mb-1">Hook (Opening line)</label>
            <textarea
              value={form.hook}
              onChange={(e) => handleChange('hook', e.target.value)}
              placeholder="Everything you built is burning. This is not punishment. This is liberation."
              rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
          </div>

          {/* Content Sections */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-medium">Content Sections</h4>
              <button onClick={addSection} className="text-sm text-amber-400 hover:text-amber-300">+ Add Section</button>
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-2">
              {form.sections.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSection(idx)}
                  className={`px-3 py-1 rounded text-sm whitespace-nowrap ${activeSection === idx ? 'bg-amber-600 text-white' : 'bg-gray-800 text-gray-400'}`}
                >
                  Section {idx + 1}
                </button>
              ))}
            </div>

            {form.sections[activeSection] && (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <input
                    type="text"
                    value={form.sections[activeSection].heading}
                    onChange={(e) => handleSectionChange(activeSection, 'heading', e.target.value)}
                    placeholder="Section Heading"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                  {form.sections.length > 1 && (
                    <button onClick={() => removeSection(activeSection)} className="ml-2 p-2 text-red-400 hover:text-red-300">
                      <Trash size={18} />
                    </button>
                  )}
                </div>
                <textarea
                  value={form.sections[activeSection].content}
                  onChange={(e) => handleSectionChange(activeSection, 'content', e.target.value)}
                  placeholder="Section content... (supports **bold** and *italic*)"
                  rows={6}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-mono text-sm"
                />
              </div>
            )}
          </div>

          {/* FAQ */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-medium">FAQ (Schema.org)</h4>
              <button onClick={addFaq} className="text-sm text-amber-400 hover:text-amber-300">+ Add FAQ</button>
            </div>
            {form.faq.map((item, idx) => (
              <div key={idx} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item.question}
                    onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
                    placeholder="Question"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <button onClick={() => removeFaq(idx)} className="p-2 text-red-400 hover:text-red-300">
                    <Trash size={16} />
                  </button>
                </div>
                <textarea
                  value={item.answer}
                  onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
                  placeholder="Answer"
                  rows={2}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            ))}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-gray-400 text-sm mb-1 flex items-center gap-2">
              <Tag size={16} />
              Tags (comma separated)
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="major arcana, transformation, crisis"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-gray-400 text-sm mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-400 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.slug || !form.title}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && <SpinnerGap size={18} className="animate-spin" />}
            {guide ? 'Save Changes' : 'Create Guide'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Admin Panel
const AdminPanel = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('all');

  const loadAnalytics = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/analytics?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        setToken(null);
      }
    }
  }, [token, period]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const handleResetStats = async () => {
    if (!window.confirm('Clear ALL test user data? (users, sessions, divinations, events)')) return;
    try {
      await axios.post(`${API}/admin/reset-stats`, { collections: ["users", "sessions", "divinations", "events", "promo_redemptions"] }, { headers: { Authorization: `Bearer ${token}` } });
      loadAnalytics();
    } catch (err) {
      alert('Reset failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  if (!token) return <AdminLogin onLogin={setToken} />;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: ChartBar },
    { id: 'ai-config', label: 'AI Config', icon: Gear },
    { id: 'telegram', label: 'Telegram', icon: PaperPlaneTilt },
    { id: 'seo-guides', label: 'SEO Guides', icon: Article },
    { id: 'promos', label: 'Promo Codes', icon: Ticket },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'security', label: 'Security', icon: ShieldWarning },
  ];

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col">
        <div className="p-6 border-b border-gray-800">
          <h1 className="text-lg font-bold text-white tracking-wide">ONIMA</h1>
          <p className="text-gray-500 text-xs mt-0.5">Admin Panel</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors
                ${activeTab === tab.id ? 'bg-amber-600/20 text-amber-400' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon size={20} />
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-800">
          <a href="/" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
            <ArrowLeft size={20} />
            Back to App
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
            data-testid="admin-logout-btn"
          >
            <SignOut size={20} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {activeTab === 'dashboard' && <DashboardTab analytics={analytics} period={period} setPeriod={setPeriod} onResetStats={handleResetStats} />}
          {activeTab === 'ai-config' && <AIConfigTab token={token} />}
          {activeTab === 'telegram' && <TelegramTab token={token} />}
          {activeTab === 'seo-guides' && <SEOGuidesTab token={token} />}
          {activeTab === 'promos' && <PromoCodesTab token={token} />}
          {activeTab === 'users' && <UsersTab token={token} />}
          {activeTab === 'security' && <SecurityTab token={token} />}
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
